// ════════════════════════════════════════════════════════════
// Proformas — propuestas económicas sin validez fiscal
// Se pueden editar mientras no se conviertan en factura.
// ════════════════════════════════════════════════════════════
import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, TextField, Button, Chip, Alert, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Select, MenuItem,
  FormControl, InputLabel
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import {
  getProformas, getProforma, actualizarProforma, anularProforma,
  convertirProformaEnFactura, descargarProformaPDF, getConfigERP
} from '../api';

const ESTADOS: any = {
  pendiente: { label: 'Pendiente', color: 'warning' },
  facturada: { label: 'Facturada', color: 'success' },
  anulada:   { label: 'Anulada',   color: 'default' },
};
const eur = (n: number) => (n || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
const r2 = (n: number) => Math.round(n * 100) / 100;

export default function Proformas() {
  const [items, setItems] = useState<any[]>([]);
  const [vista, setVista] = useState(0);
  const [search, setSearch] = useState('');
  const [cargando, setCargando] = useState(true);
  const [editor, setEditor] = useState<any>(null);
  const [lineas, setLineas] = useState<any[]>([]);
  const [formas, setFormas] = useState<any[]>([]);
  const [procesando, setProcesando] = useState(false);

  const cargar = () => {
    setCargando(true);
    getProformas({ search }).then(setItems).catch(() => setItems([]))
      .finally(() => setCargando(false));
  };
  useEffect(() => {
    getConfigERP().then((c: any) => setFormas(c.formas_pago || [])).catch(() => {});
  }, []);
  useEffect(() => {
    const t = setTimeout(cargar, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const pendientes = items.filter((x: any) => x.estado === 'pendiente');
  const otras = items.filter((x: any) => x.estado !== 'pendiente');
  const lista = vista === 0 ? pendientes : vista === 1 ? otras : items;

  const abrir = async (p: any) => {
    try {
      const d = await getProforma(p.id);
      setEditor(d);
      setLineas((d.lineas || []).map((l: any) => ({ ...l })));
    } catch { alert('No se pudo cargar la proforma'); }
  };

  const setLinea = (i: number, campo: string, valor: any) => {
    const nuevas = [...lineas];
    nuevas[i] = { ...nuevas[i], [campo]: valor };
    setLineas(nuevas);
  };

  let base = 0;
  let cuota = 0;
  lineas.forEach(l => {
    const b = (parseFloat(l.cantidad) || 0) * (parseFloat(l.precio_unitario) || 0)
      * (1 - (parseFloat(l.descuento) || 0) / 100);
    base += b;
    cuota += b * (parseFloat(l.tipo_iva) || 0) / 100;
  });
  base = r2(base);
  cuota = r2(cuota);

  const guardar = async () => {
    setProcesando(true);
    try {
      await actualizarProforma(editor.id, {
        fecha: editor.fecha, validez_dias: editor.validez_dias,
        forma_pago: editor.forma_pago, observaciones: editor.observaciones,
        lineas: lineas.filter(l => (l.concepto || '').trim()).map(l => ({
          concepto: l.concepto.trim(), descripcion: l.descripcion,
          cantidad: parseFloat(l.cantidad) || 0,
          precio_unitario: parseFloat(l.precio_unitario) || 0,
          descuento: parseFloat(l.descuento) || 0, tipo_iva: parseFloat(l.tipo_iva) || 0,
        })),
      });
      setEditor(null);
      cargar();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Error al guardar');
    } finally { setProcesando(false); }
  };

  const facturar = async () => {
    if (!window.confirm(
      `¿Convertir la proforma ${editor.numero} en factura?\n\n` +
      `Se emitirá con numeración legal y ya no se podrá modificar la proforma.`)) return;
    setProcesando(true);
    try {
      const r = await convertirProformaEnFactura(editor.id, { forma_pago: editor.forma_pago });
      alert(`✅ Factura ${r.numero} emitida.`);
      setEditor(null);
      cargar();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Error al facturar');
    } finally { setProcesando(false); }
  };

  const anular = async (p: any) => {
    if (!window.confirm(`¿Anular la proforma ${p.numero}?`)) return;
    try { await anularProforma(p.id); setEditor(null); cargar(); }
    catch (e: any) { alert(e.response?.data?.detail || 'Error al anular'); }
  };

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6}>
          <Card sx={{ borderRadius: 3, borderLeft: '4px solid #f59e0b' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Pendientes de aceptar</Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#f59e0b' }}>
                {pendientes.length} · {eur(pendientes.reduce((s, x) => s + (x.total || 0), 0))}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card sx={{ borderRadius: 3, borderLeft: '4px solid #16a34a' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Convertidas en factura</Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#16a34a' }}>
                {items.filter((x: any) => x.estado === 'facturada').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Tabs value={vista} onChange={(_, v) => setVista(v)} sx={{ mb: 1 }}>
        <Tab label={`Pendientes (${pendientes.length})`} />
        <Tab label={`Cerradas (${otras.length})`} />
        <Tab label="Todas" />
      </Tabs>
      <TextField fullWidth size="small" label="Buscar (nº o cliente)" sx={{ mb: 2 }}
        value={search} onChange={(e) => setSearch(e.target.value)} />

      {cargando ? <CircularProgress /> : lista.length === 0 ? (
        <Alert severity="info">
          No hay proformas. Se crean desde la propuesta de una intervención o desde una venta.
        </Alert>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3, overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Número</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Cliente</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Fecha</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Válida hasta</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Total</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Estado</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lista.map((p: any) => {
                const est = ESTADOS[p.estado] || { label: p.estado, color: 'default' };
                return (
                  <TableRow key={p.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{p.numero}</TableCell>
                    <TableCell>{p.cliente_nombre}</TableCell>
                    <TableCell>{p.fecha}</TableCell>
                    <TableCell sx={{ color: p.caducada ? 'error.main' : 'inherit' }}>
                      {p.fecha_validez || '—'}{p.caducada ? ' · caducada' : ''}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{eur(p.total)}</TableCell>
                    <TableCell><Chip size="small" label={est.label} color={est.color} /></TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Button size="small" onClick={() => descargarProformaPDF(p.id, p.numero)}>📄 PDF</Button>
                      <Button size="small" variant={p.estado === 'pendiente' ? 'contained' : 'text'}
                        sx={p.estado === 'pendiente' ? { bgcolor: '#4f46e5' } : {}}
                        onClick={() => abrir(p)}>
                        {p.estado === 'pendiente' ? 'Abrir' : 'Ver'}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* EDITOR */}
      <Dialog open={!!editor} maxWidth="lg" fullWidth
        onClose={(e, r) => { if (r !== 'backdropClick' && r !== 'escapeKeyDown') setEditor(null); }}>
        {editor && (() => {
          const cerrada = editor.estado !== 'pendiente';
          return (
            <>
              <DialogTitle>
                📄 Proforma {editor.numero} — {editor.cliente_nombre}
                {cerrada && <Chip size="small" sx={{ ml: 1 }}
                  label={ESTADOS[editor.estado]?.label} color={ESTADOS[editor.estado]?.color} />}
              </DialogTitle>
              <DialogContent>
                <Grid container spacing={2} sx={{ mt: 0.5, mb: 2 }}>
                  <Grid item xs={6} sm={3}>
                    <TextField fullWidth size="small" type="date" label="Fecha" disabled={cerrada}
                      InputLabelProps={{ shrink: true }} value={editor.fecha || ''}
                      onChange={(e) => setEditor({ ...editor, fecha: e.target.value })} />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField fullWidth size="small" type="number" label="Validez (días)" disabled={cerrada}
                      value={editor.validez_dias || 30}
                      onChange={(e) => setEditor({ ...editor, validez_dias: e.target.value })} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small" disabled={cerrada}>
                      <InputLabel>Forma de pago</InputLabel>
                      <Select value={formas.some((f: any) => f.clave === editor.forma_pago) ? editor.forma_pago : 'contado'}
                        label="Forma de pago"
                        onChange={(e) => setEditor({ ...editor, forma_pago: e.target.value })}>
                        {formas.map((f: any) => <MenuItem key={f.clave} value={f.clave}>{f.nombre}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 780 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Concepto</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: 90 }}>Cant.</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: 110 }}>Precio</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: 80 }}>Dto %</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: 90 }}>IVA</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">Importe</TableCell>
                        {!cerrada && <TableCell sx={{ width: 50 }} />}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lineas.map((l, i) => {
                        const importe = (parseFloat(l.cantidad) || 0) * (parseFloat(l.precio_unitario) || 0)
                          * (1 - (parseFloat(l.descuento) || 0) / 100);
                        return (
                          <TableRow key={i}>
                            <TableCell>
                              <TextField fullWidth variant="standard" value={l.concepto || ''} disabled={cerrada}
                                onChange={(e) => setLinea(i, 'concepto', e.target.value)} />
                            </TableCell>
                            <TableCell>
                              <TextField variant="standard" type="number" value={l.cantidad} disabled={cerrada}
                                onChange={(e) => setLinea(i, 'cantidad', e.target.value)} />
                            </TableCell>
                            <TableCell>
                              <TextField variant="standard" type="number" value={l.precio_unitario} disabled={cerrada}
                                onChange={(e) => setLinea(i, 'precio_unitario', e.target.value)} />
                            </TableCell>
                            <TableCell>
                              <TextField variant="standard" type="number" value={l.descuento} disabled={cerrada}
                                onChange={(e) => setLinea(i, 'descuento', e.target.value)} />
                            </TableCell>
                            <TableCell>
                              <Select variant="standard" fullWidth value={Number(l.tipo_iva)} disabled={cerrada}
                                onChange={(e) => setLinea(i, 'tipo_iva', e.target.value)}>
                                {[21, 10, 4, 0].map(p => <MenuItem key={p} value={p}>{p}%</MenuItem>)}
                              </Select>
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>{eur(importe)}</TableCell>
                            {!cerrada && (
                              <TableCell>
                                <IconButton size="small" color="error"
                                  onClick={() => setLineas(lineas.filter((_, j) => j !== i))}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                {!cerrada && (
                  <Button size="small" startIcon={<AddIcon />} sx={{ mt: 1 }}
                    onClick={() => setLineas([...lineas, { concepto: '', cantidad: 1,
                      precio_unitario: 0, descuento: 0, tipo_iva: 21 }])}>
                    Añadir línea
                  </Button>
                )}

                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={7}>
                    <TextField fullWidth multiline rows={2} size="small" label="Observaciones"
                      disabled={cerrada} value={editor.observaciones || ''}
                      onChange={(e) => setEditor({ ...editor, observaciones: e.target.value })} />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Base imponible</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{eur(base)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">IVA</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{eur(cuota)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                        <Typography sx={{ fontWeight: 700 }}>TOTAL</Typography>
                        <Typography sx={{ fontWeight: 800 }}>{eur(base + cuota)}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
                {!cerrada && (
                  <Button color="error" onClick={() => anular(editor)}>Anular</Button>
                )}
                <Box sx={{ flex: 1 }} />
                <Button onClick={() => setEditor(null)}>Cerrar</Button>
                <Button onClick={() => descargarProformaPDF(editor.id, editor.numero)}>📄 PDF</Button>
                {!cerrada && (
                  <>
                    <Button variant="outlined" onClick={guardar} disabled={procesando}>
                      Guardar cambios
                    </Button>
                    <Button variant="contained" onClick={facturar} disabled={procesando}
                      sx={{ bgcolor: '#22c55e' }}>
                      ✅ Convertir en factura
                    </Button>
                  </>
                )}
              </DialogActions>
            </>
          );
        })()}
      </Dialog>
    </Box>
  );
}
